/**
 * One step of a workflow in the builder: a card with its number, title, condition and problems; opened, its fields
 * (per step type) and its condition ("Only run this step if…", Slack's branching). An app step picks an installed
 * app and one of the steps it declares (Slack's functions), then fills that step's declared inputs.
 */
import type { ConditionOp, FormField, FormFieldKind, WorkflowCondition, WorkflowDefinition, WorkflowRecipient, WorkflowStep } from "@bundle/contracts";
import { MAX_FORM_FIELDS } from "@bundle/contracts";
import { WorkflowBuilder, Workflows, type WorkflowProblem } from "@bundle/core";
import { useMemo, useState } from "react";
import { Button, Card, Checkbox, IconButton, Select, Stack, Text, TextField } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";
import { usePageTree, useWorkspace } from "../../hooks/index.ts";
import { TemplateField, useChipLabel, useLabel } from "./TemplateField.tsx";

const FIELD_KINDS: readonly FormFieldKind[] = ["text", "paragraph", "number", "select", "user", "channel", "date"];
const DELAY_UNITS = ["minutes", "hours", "days"] as const;

export type StepEditorProps = {
  def: WorkflowDefinition;
  step: WorkflowStep;
  index: number;
  open: boolean;
  problems: WorkflowProblem[];
  /** Required fields are empty (can't save yet). */
  incomplete: boolean;
  onToggle: () => void;
  onChange: (step: WorkflowStep) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
  /** Installed apps that offer workflow steps (app steps' picker). */
  appChoices?: WorkflowBuilder.AppStepChoice[];
  /** What's wrong with this app step (app gone, step gone, required inputs empty). */
  appIssues?: WorkflowBuilder.AppStepIssue[];
  /** An app step's app and declared step were picked. */
  onChooseAppStep?: (appId: string, callbackId: string) => void;
  testID: string;
};

export function StepEditor(p: StepEditorProps) {
  const t = useT();
  const tr = useTr();
  const label = useLabel();
  const { step } = p;
  const id = `${p.testID}-${step.id}`;
  return (
    <Card gap="3" testID={id}>
      <Stack direction="horizontal" align="center" gap="2">
        <Stack grow gap="0.5">
          <Text weight="semibold" lines={1}>{`${p.index + 1}. ${label(WorkflowBuilder.stepTitle(step))}`}</Text>
          {step.condition === undefined ? null : (
            <Text variant="caption" color="text.secondary" lines={1}>
              {t("workflow.condition.summary")}
            </Text>
          )}
        </Stack>
        <IconButton icon="arrowUp" size="sm" accessibilityLabel={t("workflow.step.moveUp")} onPress={() => p.onMove(-1)} disabled={p.index === 0} testID={`${id}-up`} />
        <IconButton icon="arrowDown" size="sm" accessibilityLabel={t("workflow.step.moveDown")} onPress={() => p.onMove(1)} disabled={p.index === p.def.steps.length - 1} testID={`${id}-down`} />
        <IconButton icon="trash" size="sm" accessibilityLabel={t("workflow.step.delete")} onPress={p.onRemove} testID={`${id}-delete`} />
        <Button label={p.open ? t("workflow.step.done") : t("workflow.step.edit")} size="sm" variant="secondary" onPress={p.onToggle} testID={`${id}-toggle`} />
      </Stack>
      {p.incomplete ? (
        <Text variant="caption" color="status.warning">
          {t("workflow.step.incomplete")}
        </Text>
      ) : null}
      {p.problems.map((x, i) => (
        <Text key={i} variant="caption" color="status.danger" testID={`${id}-problem-${i}`}>
          {tr(WorkflowBuilder.problemMessage(x))}
        </Text>
      ))}
      {(p.appIssues ?? []).map((x, i) => (
        <Text key={i} variant="caption" color="status.danger" testID={`${id}-app-issue-${i}`}>
          {tr(WorkflowBuilder.appStepIssueMessage(x))}
        </Text>
      ))}
      {p.open ? (
        <Stack gap="3" testID={`${id}-fields`}>
          {step.type === "app_step" ? (
            <AppStepFields def={p.def} step={step} onChange={p.onChange} choices={p.appChoices ?? []} onChoose={p.onChooseAppStep} testID={id} />
          ) : (
            <StepFields def={p.def} step={step} onChange={p.onChange} testID={id} />
          )}
          <ConditionEditor def={p.def} step={step} onChange={p.onChange} testID={`${id}-condition`} />
        </Stack>
      ) : null}
    </Card>
  );
}

function useChannelOptions() {
  const ws = useWorkspace();
  const cache = ws.state.cache;
  return useMemo(() => (cache === undefined ? [] : WorkflowBuilder.workflowChannels(cache).map((c) => ({ value: c.id, label: c.name, icon: c.private ? ("lock" as const) : ("hash" as const) }))), [cache]);
}

function usePeopleOptions() {
  const ws = useWorkspace();
  const cache = ws.state.cache;
  return useMemo(() => (cache === undefined ? [] : WorkflowBuilder.workflowPeople(cache).map((m) => ({ value: m.id, label: m.name }))), [cache]);
}

/** Variables of some types a step can use, as select options (value = the template `{{ref}}`). */
function useVariableOptions(def: WorkflowDefinition, stepId: string, types: readonly string[]) {
  const chipLabel = useChipLabel();
  return Workflows.availableVariables(def, stepId)
    .filter((v) => types.includes(v.type))
    .map((v) => ({ value: Workflows.variableToken(v.ref), label: chipLabel(WorkflowBuilder.variableChip(def, v.ref, stepId)) }));
}

type FieldsProps = { def: WorkflowDefinition; step: WorkflowStep; onChange: (s: WorkflowStep) => void; testID: string };

function StepFields({ def, step, onChange, testID }: FieldsProps) {
  switch (step.type) {
    case "send_message":
      return <SendMessageFields def={def} step={step} onChange={onChange} testID={testID} />;
    case "form":
      return <FormFields step={step} onChange={onChange} testID={testID} />;
    case "add_reaction":
      return <ReactionFields def={def} step={step} onChange={onChange} testID={testID} />;
    case "create_page":
      return <PageFields def={def} step={step} onChange={onChange} testID={testID} />;
    case "create_row":
      return <RowFields def={def} step={step} onChange={onChange} testID={testID} />;
    case "add_to_channel":
      return <AddToChannelFields def={def} step={step} onChange={onChange} testID={testID} />;
    case "delay":
      return <DelayFields step={step} onChange={onChange} testID={testID} />;
    case "app_step":
      return null;
  }
}

type RecipientKind = WorkflowRecipient["kind"];

function SendMessageFields({ def, step, onChange, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "send_message" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const channels = useChannelOptions();
  const people = usePeopleOptions();
  const vars = useVariableOptions(def, step.id, ["user", "channel"]);
  const kinds: RecipientKind[] = Workflows.hasRunner(def.trigger) ? ["runner", "channel", "user", "variable"] : ["channel", "user", "variable"];
  const to = step.to;
  const setKind = (kind: RecipientKind) => {
    const next: WorkflowRecipient =
      kind === "runner" ? { kind } : kind === "channel" ? { kind, channelId: "channelId" in def.trigger ? def.trigger.channelId : "" } : kind === "user" ? { kind, userId: "" } : { kind, ref: "" };
    onChange({ ...step, to: next });
  };
  return (
    <Stack gap="3">
      <Select label={t("workflow.message.to")} value={to.kind} onChange={setKind} options={kinds.map((k) => ({ value: k, label: t(`workflow.message.to.${k}`) }))} testID={`${testID}-to`} />
      {to.kind === "channel" ? (
        <Select accessibilityLabel={t("workflow.message.to.channel")} placeholder={t("workflow.channel.choose")} value={to.channelId === "" ? undefined : to.channelId} onChange={(channelId) => onChange({ ...step, to: { kind: "channel", channelId } })} options={channels} testID={`${testID}-to-channel`} />
      ) : to.kind === "user" ? (
        <Select accessibilityLabel={t("workflow.message.to.user")} placeholder={t("workflow.person.choose")} value={to.userId === "" ? undefined : to.userId} onChange={(userId) => onChange({ ...step, to: { kind: "user", userId } })} options={people} testID={`${testID}-to-user`} />
      ) : to.kind === "variable" ? (
        <Select
          accessibilityLabel={t("workflow.message.to.variable")}
          placeholder={t("workflow.variable.choose")}
          value={to.ref === "" ? undefined : Workflows.variableToken(to.ref)}
          onChange={(token) => onChange({ ...step, to: { kind: "variable", ref: Workflows.templateRefs(token)[0] ?? "" } })}
          options={vars}
          testID={`${testID}-to-variable`}
        />
      ) : null}
      <TemplateField label={t("workflow.message.text")} value={step.text} onChange={(text) => onChange({ ...step, text })} def={def} stepId={step.id} placeholder={t("workflow.message.placeholder")} testID={`${testID}-text`} />
    </Stack>
  );
}

function FormFields({ step, onChange, testID }: { step: Extract<WorkflowStep, { type: "form" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const setField = (i: number, f: FormField) => onChange({ ...step, fields: step.fields.map((x, j) => (j === i ? f : x)) });
  const addField = () => {
    let n = step.fields.length + 1;
    while (step.fields.some((f) => f.id === `question${n}`)) n += 1;
    onChange({ ...step, fields: [...step.fields, { id: `question${n}`, label: t("workflow.form.question"), kind: "text", required: false }] });
  };
  return (
    <Stack gap="3">
      <TextField label={t("workflow.form.title")} value={step.title} onChange={(title) => onChange({ ...step, title: title.slice(0, 24) })} testID={`${testID}-title`} />
      <TextField label={t("workflow.form.submit")} value={step.submitLabel ?? ""} placeholder={t("appModal.submit")} onChange={(v) => onChange(v === "" ? { ...step, submitLabel: undefined } : { ...step, submitLabel: v.slice(0, 24) })} testID={`${testID}-submit`} />
      {step.fields.map((f, i) => (
        <Stack key={i} gap="2" padding="3" radius="md" border="border.subtle" testID={`${testID}-field-${i}`}>
          <Stack direction="horizontal" gap="2" align="end">
            <Stack grow>
              <TextField label={t("workflow.form.fieldLabel")} value={f.label} onChange={(label) => setField(i, { ...f, label })} testID={`${testID}-field-${i}-label`} />
            </Stack>
            <Select label={t("workflow.form.fieldKind")} value={f.kind} onChange={(kind) => setField(i, kind === "select" ? { ...f, kind, options: f.options ?? [t("workflow.form.option", { n: 1 })] } : { ...f, kind, options: undefined })} options={FIELD_KINDS.map((k) => ({ value: k, label: t(`workflow.form.kind.${k}`) }))} testID={`${testID}-field-${i}-kind`} />
            <IconButton icon="trash" accessibilityLabel={t("workflow.form.removeField")} onPress={() => onChange({ ...step, fields: step.fields.filter((_x, j) => j !== i) })} disabled={step.fields.length === 1} testID={`${testID}-field-${i}-remove`} />
          </Stack>
          <Stack direction="horizontal" gap="3" align="center">
            <Checkbox label={t("workflow.form.required")} value={f.required} onChange={(required) => setField(i, { ...f, required })} testID={`${testID}-field-${i}-required`} />
            <Text variant="caption" color="text.tertiary">
              {t("workflow.form.fieldId", { id: f.id })}
            </Text>
          </Stack>
          {f.kind === "select" ? (
            <TextField
              label={t("workflow.form.options")}
              helperText={t("workflow.form.options.help")}
              multiline
              value={(f.options ?? []).join("\n")}
              onChange={(v) => setField(i, { ...f, options: v.split("\n").slice(0, 20) })}
              testID={`${testID}-field-${i}-options`}
            />
          ) : null}
        </Stack>
      ))}
      <Stack direction="horizontal">
        <Button label={t("workflow.form.addField")} icon="plus" variant="ghost" size="sm" onPress={addField} disabled={step.fields.length >= MAX_FORM_FIELDS} testID={`${testID}-add-field`} />
      </Stack>
    </Stack>
  );
}

function ReactionFields({ def, step, onChange, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "add_reaction" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const messages = useVariableOptions(def, step.id, ["message"]);
  return (
    <Stack gap="3">
      <Select label={t("workflow.reaction.message")} placeholder={t("workflow.variable.choose")} value={step.message === "" ? undefined : step.message} onChange={(message) => onChange({ ...step, message })} options={messages} testID={`${testID}-message`} />
      <TextField label={t("workflow.reaction.emoji")} value={step.emoji} onChange={(emoji) => onChange({ ...step, emoji })} helperText={t("workflow.trigger.emoji.help")} testID={`${testID}-emoji`} />
    </Stack>
  );
}

function PageFields({ def, step, onChange, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "create_page" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  return (
    <Stack gap="3">
      <TemplateField label={t("workflow.page.title")} value={step.title} onChange={(title) => onChange({ ...step, title })} def={def} stepId={step.id} testID={`${testID}-title`} />
      <TemplateField label={t("workflow.page.text")} value={step.text ?? ""} onChange={(text) => onChange(text === "" ? { ...step, text: undefined } : { ...step, text })} def={def} stepId={step.id} testID={`${testID}-text`} />
    </Stack>
  );
}

function RowFields({ def, step, onChange, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "create_row" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const tree = usePageTree();
  const dbs = tree.state.databases.map((d) => ({ value: d.id, label: `${d.icon !== undefined && !d.icon.startsWith("icon:") ? `${d.icon} ` : ""}${d.title === "" ? t("workflow.row.untitled") : d.title}` }));
  if (step.databaseId !== "" && !dbs.some((d) => d.value === step.databaseId)) dbs.unshift({ value: step.databaseId, label: step.databaseId });
  return (
    <Stack gap="3">
      <Select label={t("workflow.row.database")} placeholder={t("workflow.row.choose")} value={step.databaseId === "" ? undefined : step.databaseId} onChange={(databaseId) => onChange({ ...step, databaseId })} options={dbs} testID={`${testID}-database`} />
      <TemplateField label={t("workflow.row.title")} value={step.title} onChange={(title) => onChange({ ...step, title })} def={def} stepId={step.id} testID={`${testID}-title`} />
    </Stack>
  );
}

function AddToChannelFields({ def, step, onChange, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "add_to_channel" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const channels = useChannelOptions();
  const people = usePeopleOptions();
  const vars = useVariableOptions(def, step.id, ["user"]);
  return (
    <Stack gap="3">
      <Select label={t("workflow.addToChannel.channel")} placeholder={t("workflow.channel.choose")} value={step.channelId === "" ? undefined : step.channelId} onChange={(channelId) => onChange({ ...step, channelId })} options={channels} testID={`${testID}-channel`} />
      <Select label={t("workflow.addToChannel.user")} placeholder={t("workflow.person.choose")} value={step.user === "" ? undefined : step.user} onChange={(user) => onChange({ ...step, user })} options={[...vars, ...people]} testID={`${testID}-user`} />
    </Stack>
  );
}

function DelayFields({ step, onChange, testID }: { step: Extract<WorkflowStep, { type: "delay" }>; onChange: (s: WorkflowStep) => void; testID: string }) {
  const t = useT();
  const { unit, count } = Workflows.delayParts(step.minutes);
  return (
    <Stack direction="horizontal" gap="2" align="end">
      <Stack grow>
        <TextField label={t("workflow.delay.count")} keyboard="number" value={String(count)} onChange={(v) => onChange({ ...step, minutes: Math.min(WorkflowBuilder.delayMinutes(unit, Number(v)), 30 * 24 * 60) })} testID={`${testID}-count`} />
      </Stack>
      <Select accessibilityLabel={t("workflow.delay.unit")} value={unit} onChange={(u) => onChange({ ...step, minutes: Math.min(WorkflowBuilder.delayMinutes(u, count), 30 * 24 * 60) })} options={DELAY_UNITS.map((u) => ({ value: u, label: t(`workflow.delay.${u}`) }))} testID={`${testID}-unit`} />
    </Stack>
  );
}

/**
 * An app step: pick an installed app and one of its declared steps (Slack's "Add step" → app → step); then one field
 * per declared input (required ones marked) and the outputs it sends back, read-only. A step whose app or declaration
 * is gone keeps its own inputs editable.
 */
function AppStepFields({ def, step, onChange, choices, onChoose, testID }: { def: WorkflowDefinition; step: Extract<WorkflowStep, { type: "app_step" }>; onChange: (s: WorkflowStep) => void; choices: WorkflowBuilder.AppStepChoice[]; onChoose: ((appId: string, callbackId: string) => void) | undefined; testID: string }) {
  const t = useT();
  const [pickedApp, setPickedApp] = useState<string | undefined>(step.appId === "" ? undefined : step.appId);
  const appId = step.appId === "" ? pickedApp : step.appId;
  const choice = choices.find((c) => c.appId === appId);
  const decl = choice?.steps.find((d) => d.callbackId === step.callbackId);
  const appOptions = choices.map((c) => ({ value: c.appId, label: c.appName }));
  if (appId !== undefined && choice === undefined) appOptions.unshift({ value: appId, label: appId });
  const stepOptions = (choice?.steps ?? []).map((d) => ({ value: d.callbackId, label: d.title }));
  if (step.callbackId !== "" && decl === undefined && step.appId === appId) stepOptions.unshift({ value: step.callbackId, label: step.title || step.callbackId });
  if (choices.length === 0 && step.appId === "") {
    return (
      <Text variant="footnote" color="text.secondary" testID={`${testID}-no-apps`}>
        {t("workflow.app.noApps")}
      </Text>
    );
  }
  const declared = decl?.inputs ?? Object.keys(step.inputs).map((name) => ({ name, label: name, type: "text" as const, required: false }));
  const setInput = (name: string, v: string) => onChange({ ...step, inputs: { ...step.inputs, [name]: v } });
  return (
    <Stack gap="3">
      <Stack direction="horizontal" gap="2">
        <Stack grow>
          <Select
            label={t("workflow.app.chooseApp")}
            placeholder={t("workflow.app.chooseAppPlaceholder")}
            value={appId}
            onChange={(v) => {
              setPickedApp(v);
              const only = choices.find((c) => c.appId === v)?.steps;
              if (only?.length === 1 && onChoose !== undefined) onChoose(v, only[0]!.callbackId);
            }}
            options={appOptions}
            testID={`${testID}-app`}
          />
        </Stack>
        <Stack grow>
          <Select
            label={t("workflow.app.chooseStep")}
            placeholder={t("workflow.app.chooseStepPlaceholder")}
            value={step.callbackId === "" || step.appId !== appId ? undefined : step.callbackId}
            onChange={(callbackId) => (appId === undefined ? undefined : onChoose?.(appId, callbackId))}
            options={stepOptions}
            disabled={appId === undefined}
            testID={`${testID}-callback`}
          />
        </Stack>
      </Stack>
      {step.appId === "" ? null : (
        <>
          {decl?.description === undefined ? null : (
            <Text variant="footnote" color="text.secondary" testID={`${testID}-description`}>
              {decl.description}
            </Text>
          )}
          <TextField label={t("workflow.app.title")} value={step.title} onChange={(title) => onChange({ ...step, title: title.slice(0, 80) })} testID={`${testID}-title`} />
          <Text variant="footnote" weight="medium" color="text.secondary">
            {t("workflow.app.inputs")}
          </Text>
          {declared.length === 0 ? (
            <Text variant="caption" color="text.tertiary">
              {t("workflow.app.noInputs")}
            </Text>
          ) : null}
          {declared.map((input) => (
            <TemplateField
              key={input.name}
              label={input.required === true ? t("workflow.app.inputRequired", { label: input.label }) : input.label}
              value={step.inputs[input.name] ?? ""}
              onChange={(v) => setInput(input.name, v)}
              def={def}
              stepId={step.id}
              testID={`${testID}-input-${input.name}`}
            />
          ))}
          {step.outputs.length === 0 ? null : (
            <Stack gap="1" testID={`${testID}-outputs`}>
              <Text variant="footnote" weight="medium" color="text.secondary">
                {t("workflow.app.outputs")}
              </Text>
              <Text variant="caption" color="text.tertiary">
                {t("workflow.app.outputsHelp")}
              </Text>
              {step.outputs.map((o) => (
                <Stack key={o.name} direction="horizontal" gap="2" align="center">
                  <Text variant="footnote" mono>
                    {o.name}
                  </Text>
                  <Text variant="caption" color="text.secondary">
                    {t(`workflow.varType.${o.type}`)}
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}

/** "Only run this step if <variable> <op> <value>" (Slack's conditional branching). */
function ConditionEditor({ def, step, onChange, testID }: FieldsProps) {
  const t = useT();
  const tr = useTr();
  const chipLabel = useChipLabel();
  const c = step.condition;
  const vars = Workflows.availableVariables(def, step.id).map((v) => ({ value: Workflows.variableToken(v.ref), label: chipLabel(WorkflowBuilder.variableChip(def, v.ref, step.id)) }));
  const set = (condition: WorkflowCondition | undefined) => {
    const { condition: _old, ...rest } = step;
    onChange((condition === undefined ? rest : { ...rest, condition }) as WorkflowStep);
  };
  if (c === undefined) {
    return (
      <Stack direction="horizontal">
        <Button label={t("workflow.condition.add")} icon="filter" variant="ghost" size="sm" onPress={() => set({ value: vars[0]?.value ?? "", op: "is", compare: "" })} disabled={vars.length === 0} testID={`${testID}-add`} />
      </Stack>
    );
  }
  if (!c.value.startsWith("{{") && c.value !== "") vars.unshift({ value: c.value, label: c.value });
  return (
    <Stack gap="2" padding="3" radius="md" background="bg.sunken" testID={testID}>
      <Stack direction="horizontal" align="center" justify="between">
        <Text variant="footnote" weight="medium" color="text.secondary">
          {t("workflow.condition.title")}
        </Text>
        <IconButton icon="close" size="sm" accessibilityLabel={t("workflow.condition.remove")} onPress={() => set(undefined)} testID={`${testID}-remove`} />
      </Stack>
      <Select accessibilityLabel={t("workflow.condition.value")} placeholder={t("workflow.variable.choose")} value={c.value === "" ? undefined : c.value} onChange={(value) => set({ ...c, value })} options={vars} testID={`${testID}-value`} />
      <Select<ConditionOp>
        accessibilityLabel={t("workflow.condition.op")}
        value={c.op}
        onChange={(op) => set(WorkflowBuilder.conditionNeedsCompare(op) ? { ...c, op } : { value: c.value, op })}
        options={WorkflowBuilder.CONDITION_OPS.map((op) => ({ value: op, label: tr(WorkflowBuilder.conditionOpLabel(op)) }))}
        testID={`${testID}-op`}
      />
      {WorkflowBuilder.conditionNeedsCompare(c.op) ? (
        <TemplateField label={t("workflow.condition.compare")} value={c.compare ?? ""} onChange={(compare) => set({ ...c, compare })} def={def} stepId={step.id} testID={`${testID}-compare`} />
      ) : null}
    </Stack>
  );
}
